import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowLeft, AlertCircle, ArrowRight, MousePointer, Award, Eye } from 'lucide-react'
import { castVote, getPhotos } from '../lib/api'
import { Button } from '@/components/ui/Button'
import { PhotoLightbox } from '@/components/voting/PhotoLightbox'
import { VotingControls } from '@/components/voting/VotingControls'
import { VoteConfirmationModal } from '@/components/voting/VoteConfirmationModal'

interface Photo {
  id: string
  photoTitle: string
  name: string
  grade: string
  photoUrl: string
  thumbnailUrl?: string
  lightboxUrl?: string
}

interface LocationState {
  email: string
  photos: Photo[]
}

// Fisher-Yates shuffle for unbiased randomization
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export default function VotePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState | null

  const [photos, setPhotos] = useState<Photo[]>([])
  const [email, setEmail] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showInstructions, setShowInstructions] = useState(true)

  // Track loaded images for skeleton states
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set())

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Selection state
  const [selections, setSelections] = useState<{
    first: string | null
    second: string | null
    third: string | null
  }>({
    first: null,
    second: null,
    third: null,
  })

  useEffect(() => {
    if (!state?.email || !state?.photos) {
      setError('Please enter your email to vote')
      setIsLoading(false)
      return
    }

    setEmail(state.email)
    // Shuffle photos for each voter to reduce position bias
    const shuffled = shuffleArray(state.photos)
    setPhotos(shuffled)
    setIsLoading(false)

    // Fetch fresh photo data to get thumbnail URLs (in case state is stale)
    getPhotos()
      .then((result) => {
        if (result.data?.photos) {
          const photoMap = new Map(result.data.photos.map((p: Photo) => [p.id, p]))
          setPhotos((prev) =>
            prev.map((p) => {
              const fresh = photoMap.get(p.id)
              return fresh ? { ...p, thumbnailUrl: fresh.thumbnailUrl, lightboxUrl: fresh.lightboxUrl } : p
            })
          )
        }
      })
      .catch(() => {})
  }, [state])

  const getPhotoRank = (photoId: string): 1 | 2 | 3 | null => {
    if (selections.first === photoId) return 1
    if (selections.second === photoId) return 2
    if (selections.third === photoId) return 3
    return null
  }

  const handleVote = (photoId: string, rank: 1 | 2 | 3) => {
    const currentRank = getPhotoRank(photoId)
    const rankKey = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third'

    // If clicking the same rank on the same photo, deselect
    if (currentRank === rank) {
      setSelections((prev) => ({ ...prev, [rankKey]: null }))
      return
    }

    // If this photo already has a different rank, clear it first
    const newSelections = { ...selections }
    if (currentRank) {
      const oldRankKey = currentRank === 1 ? 'first' : currentRank === 2 ? 'second' : 'third'
      newSelections[oldRankKey] = null
    }

    // Assign the new rank (overwrites any existing selection for this rank)
    newSelections[rankKey] = photoId
    setSelections(newSelections)
  }

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  // Show confirmation modal before submitting
  const handleSubmit = () => {
    if (!selections.first || !selections.second || !selections.third) {
      setError('Please select your top 3 photos')
      return
    }

    setError(null)
    setShowConfirmation(true)
  }

  // Actually submit the votes after confirmation
  const handleConfirmSubmit = async () => {
    if (!selections.first || !selections.second || !selections.third) return
    if (!email) return

    setIsSubmitting(true)
    setError(null)

    const result = await castVote(email, selections.first, selections.second, selections.third)

    if (result.error) {
      setError(result.error)
      setIsSubmitting(false)
      setShowConfirmation(false)
      return
    }

    navigate('/vote/success')
  }

  if (isLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-accent mx-auto h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-foreground-muted mt-4">Loading photos...</p>
        </div>
      </div>
    )
  }

  if (error && photos.length === 0) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md space-y-6 text-center"
        >
          <div className="bg-error/10 border-error/20 mx-auto flex h-16 w-16 items-center justify-center rounded-full border">
            <AlertCircle className="text-error h-8 w-8" />
          </div>
          <div className="space-y-2">
            <h1 className="text-foreground font-display text-2xl font-bold">Unable to Load</h1>
            <p className="text-foreground-muted">{error}</p>
          </div>
          <Link to="/vote">
            <Button variant="secondary">Go to voting</Button>
          </Link>
        </motion.div>
      </div>
    )
  }

  const selectionCount = [selections.first, selections.second, selections.third].filter(Boolean).length
  const canSubmit = selectionCount === 3

  // Instructions screen
  if (showInstructions && photos.length > 0) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="space-y-3 text-center">
            <h1 className="text-foreground font-display text-3xl font-bold md:text-4xl">How to Vote</h1>
            <p className="text-foreground-muted">{photos.length} photos to choose from</p>
          </div>

          <div className="bg-surface border-border space-y-5 rounded-2xl border p-6">
            {[
              { icon: Eye, text: 'Browse through all the photos', detail: 'Click any photo to see it larger' },
              { icon: MousePointer, text: 'Pick your top 3 favorites', detail: 'Use the 🥇 🥈 🥉 buttons to rank' },
              { icon: Award, text: 'Submit your votes', detail: "Once you've picked 3, hit submit!" },
            ].map((step, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="bg-accent/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                  <step.icon className="text-accent h-5 w-5" />
                </div>
                <div>
                  <p className="text-foreground font-medium">{step.text}</p>
                  <p className="text-foreground-muted text-sm">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <Button size="lg" onClick={() => setShowInstructions(false)} className="w-full">
            Start Voting
            <ArrowRight className="h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen pb-36">
      {/* Header */}
      <div className="border-border bg-surface/80 sticky top-0 z-10 border-b px-4 py-4 backdrop-blur-lg">
        <div className="mx-auto max-w-5xl">
          <Link
            to="/"
            className="text-foreground-muted hover:text-foreground mb-3 inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="text-foreground font-display text-2xl font-bold">Pick Your Top 3</h1>
          <p className="text-foreground-muted mt-1 text-sm">
            Click a photo to view full size. Use the buttons to rank your favorites.
          </p>
        </div>
      </div>

      {/* Photo Grid */}
      <div className="mx-auto max-w-5xl px-4 py-6">
        {photos.length === 0 ? (
          <p className="text-foreground-muted py-12 text-center">No photos available for voting.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 pt-2 pr-2 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo, index) => {
              const rank = getPhotoRank(photo.id)

              const borderColor =
                rank === 1
                  ? 'border-[#FFD700]' // Gold
                  : rank === 2
                    ? 'border-[#C0C0C0]' // Silver
                    : rank === 3
                      ? 'border-[#CD7F32]' // Bronze
                      : 'border-border'

              return (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.3) }}
                  className="relative"
                >
                  {/* Rank badge if selected - top right corner, outside card */}
                  {rank && (
                    <div className="bg-navy absolute -top-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full text-2xl shadow-lg">
                      {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                    </div>
                  )}

                  <div
                    className={`bg-surface flex flex-col overflow-hidden rounded-xl border-2 [contain-intrinsic-size:auto_300px] [content-visibility:auto] ${borderColor}`}
                  >
                    {/* Image - clickable to open lightbox */}
                    <button
                      onClick={() => openLightbox(index)}
                      className="group relative aspect-square cursor-zoom-in overflow-hidden"
                    >
                      {/* Skeleton placeholder */}
                      {!loadedImages.has(photo.id) && (
                        <div className="bg-surface-hover absolute inset-0 animate-pulse" />
                      )}
                      <img
                        src={photo.thumbnailUrl || photo.photoUrl}
                        alt={photo.photoTitle}
                        width={400}
                        height={400}
                        decoding="async"
                        fetchPriority={index < 2 ? 'high' : undefined}
                        className={`h-full w-full object-contain transition-all duration-300 group-hover:scale-105 ${
                          loadedImages.has(photo.id) ? 'opacity-100' : 'opacity-0'
                        }`}
                        loading="lazy"
                        onLoad={() => setLoadedImages((prev) => new Set(prev).add(photo.id))}
                      />

                      {/* Hover overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                        <span className="rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                          View
                        </span>
                      </div>
                    </button>

                    {/* Info + Vote buttons */}
                    <div className="space-y-2 p-3">
                      <p className="text-foreground truncate text-sm font-medium">{photo.photoTitle}</p>

                      {/* Vote buttons */}
                      <div className="flex gap-1">
                        {([1, 2, 3] as const).map((r) => {
                          const isSelected = rank === r
                          const medal = r === 1 ? '🥇' : r === 2 ? '🥈' : '🥉'
                          const label = r === 1 ? '1st' : r === 2 ? '2nd' : '3rd'

                          return (
                            <button
                              key={r}
                              onClick={() => handleVote(photo.id, r)}
                              className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-xs font-medium transition-all ${
                                isSelected
                                  ? 'bg-accent text-accent-foreground'
                                  : 'bg-surface-hover text-foreground-muted hover:bg-accent/20 hover:text-accent'
                              } `}
                            >
                              <span>{medal}</span>
                              <span className="hidden sm:inline">{label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>

      {/* Fixed Bottom Bar */}
      <div className="border-border bg-surface/95 fixed right-0 bottom-0 left-0 z-10 border-t px-4 py-4 shadow-2xl backdrop-blur-lg">
        <div className="mx-auto max-w-5xl">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-error/10 border-error/20 text-error mb-3 rounded-xl border p-3 text-center text-sm"
            >
              {error}
            </motion.div>
          )}

          <VotingControls
            photos={photos}
            selections={selections}
            onVote={handleVote}
            onClearSelection={(rank) => {
              const rankKey = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third'
              setSelections((prev) => ({ ...prev, [rankKey]: null }))
            }}
            onPhotoClick={(photoId) => {
              const idx = photos.findIndex((p) => p.id === photoId)
              if (idx !== -1) openLightbox(idx)
            }}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            variant="horizontal"
          />
        </div>
      </div>

      {/* Lightbox */}
      <PhotoLightbox
        photos={photos}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
        selections={selections}
        onVote={handleVote}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onImageError={() => {}}
      />

      {/* Confirmation Modal */}
      <VoteConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmSubmit}
        photos={photos}
        selections={selections}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
